// src/components/ui/Dropdown.tsx
import { motion, AnimatePresence } from 'framer-motion';
import React, { useState, useRef, useEffect } from 'react';
import { IconType } from 'react-icons';

interface DropdownItem {
	label: string;
	icon?: IconType;
	onClick: () => void;
	className?: string;
	disabled?: boolean;
}

interface DropdownProps {
	trigger: React.ReactNode;
	items: DropdownItem[];
	placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
	className?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
	trigger,
	items,
	placement = 'bottom-start',
	className = ''
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	const handleItemClick = (item: DropdownItem) => {
		if (!item.disabled) {
			item.onClick();
			setIsOpen(false);
		}
	};

	const getPlacementClasses = () => {
		switch (placement) {
			case 'bottom-start':
				return 'top-full start-0 mt-2';
			case 'bottom-end':
				return 'top-full end-0 mt-2';
			case 'top-start':
				return 'bottom-full start-0 mb-2';
			case 'top-end':
				return 'bottom-full end-0 mb-2';
			default:
				return 'top-full start-0 mt-2';
		}
	};

	return (
		<div className={`relative inline-block ${className}`} ref={dropdownRef}>
			<div
				onClick={() => setIsOpen(!isOpen)}
				className="cursor-pointer"
			>
				{trigger}
			</div>

			<AnimatePresence>
				{isOpen && (
					<motion.div
						initial={{ opacity: 0, scale: 0.95, y: -10 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.95, y: -10 }}
						transition={{ duration: 0.15 }}
						className={`absolute z-50 min-w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 ${getPlacementClasses()}`}
					>
						{items.map((item, index) => {
							const Icon = item.icon;
							return (
								<button
									key={index}
									onClick={() => handleItemClick(item)}
									disabled={item.disabled}
									className={`w-full text-start px-4 py-2 text-sm flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${item.className || 'text-gray-700 dark:text-gray-200'
										}`}
								>
									{Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
									<span>{item.label}</span>
								</button>
							);
						})}
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

export default Dropdown;